/**
 * ============================================================
 * KEJJA OS — VerifyEmailPage (v2 — 6-digit OTP)
 * ============================================================
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { MailCheck, RefreshCw, Loader2, LogOut, Check, AlertCircle, KeyRound } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [sentOnce, setSentOnce] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const sendMut = useMutation({
    mutationFn: () => api.post<{ ok: true; alreadyVerified?: boolean }>('/auth/send-verification-otp', {}),
    onSuccess: (r) => {
      if (r.alreadyVerified) {
        window.location.href = '/welcome';
        return;
      }
      setSentOnce(true);
      setCooldown(60);
      setErr(null);
    },
    onError: (e) => setErr((e as Error)?.message ?? 'Could not send code'),
  });

  const verifyMut = useMutation({
    mutationFn: (c: string) => api.post<{ ok: true }>('/auth/verify-otp', { code: c }),
    onSuccess: () => {
      // Hard-reload so /auth/me re-fetches with email_verified=true
      window.location.href = '/welcome';
    },
    onError: (e) => setErr((e as Error)?.message ?? 'Invalid code'),
  });

  // Auto-send OTP on first mount
  useEffect(() => {
    if (!sentOnce && !sendMut.isPending) {
      sendMut.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (code.length !== 6) { setErr('Enter all 6 digits.'); return; }
    verifyMut.mutate(code);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 px-4 py-12">
      <div className="w-full max-w-md">

        <div className="flex flex-col items-center mb-6">
          <img src="/kejja.svg"       alt="Kejja" className="h-12 w-auto dark:hidden" />
          <img src="/kejja-white.svg" alt="Kejja" className="h-12 w-auto hidden dark:block" />
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-7">
          <div className="h-14 w-14 rounded-2xl bg-sky-100 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400 flex items-center justify-center mx-auto mb-4">
            <MailCheck className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-bold text-center mb-1.5">Verify your email</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
            {user?.email ? <>We sent a 6-digit code to <strong className="text-slate-900 dark:text-slate-100">{user.email}</strong>.</>
                         : 'We sent a 6-digit code to your email.'}
            <br/>
            Enter it below to finish creating your account.
          </p>

          {err && (
            <div className="mt-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 rounded-lg px-3 py-2 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-3">
            <input
              ref={inputRef}
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full h-14 px-4 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 text-center font-mono text-2xl tracking-[0.4em] focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition"
            />

            <button
              type="submit"
              disabled={verifyMut.isPending || code.length !== 6}
              className="w-full h-11 bg-[#196287] hover:bg-[#0e415e] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition flex items-center justify-center gap-2"
            >
              {verifyMut.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                                   : <><KeyRound className="w-4 h-4" /> Verify and continue</>}
            </button>

            <button
              type="button"
              onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || cooldown > 0}
              className="w-full h-10 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {sendMut.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                                 : cooldown > 0 ? `Resend code in ${cooldown}s`
                                 : <><RefreshCw className="w-3.5 h-3.5" /> Resend code</>}
            </button>
          </form>

          {sentOnce && cooldown > 0 && (
            <div className="mt-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded-lg px-3 py-2 text-xs flex items-start gap-2">
              <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Code sent. Check your inbox (and spam folder).</span>
            </div>
          )}

          <div className="mt-5 pt-5 border-t border-slate-200 dark:border-slate-800 text-center">
            <p className="text-[11px] text-slate-500 mb-2">Used a wrong email? Start over.</p>
            <button
              onClick={handleLogout}
              className="text-xs text-slate-500 hover:text-slate-900 dark:hover:text-white flex items-center gap-1 mx-auto"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
